import { useQuery } from '@apollo/client';
// todo: remove lodash on client
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import goldenRatio from '@glass/common/modules/goldenRatio/goldenRatio';
import isValue from '@glass/common/modules/utils/isValue';
import {
  JOB_TITLES_SECTION_ID,
  RELATED_JOB_TITLES_SECTION_ID,
} from '@glass/web/modules/content/pages/contentIds';
import useLoadingResources from '@glass/web/modules/resources/useLoadingResources';

import BrowseLink from '@glass/shared/components/Link/BrowseLink';
import ContentSection from '@glass/shared/components/Section/ContentSection';
import JobTitleGridItemLink from '@glass/shared/components/Section/JobTitlesSection/JobTitleGridItemLink';
import SectionButtonCta from '@glass/shared/components/Section/SectionButtonCta';
import SectionSpacer, { BOTTOM_EDGE } from '@glass/shared/components/Section/SectionSpacer';
import { NUM_RANDOM_JOB_TITLES } from '@glass/shared/modules/content/maxResults';
import usePageTerms from '@glass/shared/modules/content/staticVariations/usePageTerms';
import usePageSeed from '@glass/shared/modules/content/usePageSeed';
import jobTitlesQuery from '@glass/shared/modules/jobTitles/queries/jobTitles.graphql';
import { RANDOM_ORDER_BY } from '@glass/shared/modules/resources/orderBys';
import useIsBrowsePage from '@glass/shared/modules/resumes/hooks/useIsBrowsePage';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const JobTitlesSection = React.forwardRef(
  (
    {
      onItemClick,
      onSearchSuccess,
      title,
      subtitle,
      isAlternatingColor,
      type,
      disableCta,
      spacing,
      className,
    },
    ref,
  ) => {
    const { iSlug } = usePageTerms();
    const seed = usePageSeed();
    const isBrowsePage = useIsBrowsePage();

    const jobTitlesOptions = useMemo(() => {
      const variables = {
        first: NUM_RANDOM_JOB_TITLES,
        orderBy: RANDOM_ORDER_BY,
        seed,
      };
      if (iSlug && type === RELATED_JOB_TITLES_SECTION_ID) {
        variables.where = { industries_some: { slug: iSlug } };
      }
      return {
        variables,
        skip: !seed,
      };
    }, [iSlug, seed, type]);

    const { data, loading } = useQuery(jobTitlesQuery, jobTitlesOptions);

    const jobTitles = useMemo(
      () => (data?.jobTitles ? sortBy(data.jobTitles, 'name') : []),
      [data],
    );
    const loadingJobTitles = useLoadingResources(NUM_RANDOM_JOB_TITLES);

    const items = useMemo(
      () =>
        ((loading && !jobTitles?.length ? loadingJobTitles : jobTitles) || []).map(
          ({ slug, name, width, industries }) => (
            <JobTitleGridItemLink
              key={slug}
              industries={industries}
              loading={loading}
              name={name}
              slug={slug}
              width={width}
              onClick={onItemClick}
            />
          ),
        ),
      [jobTitles, loading, loadingJobTitles, onItemClick],
    );

    return (
      <ContentSection
        ref={ref}
        disableCta
        className={className}
        isAlternatingColor={isAlternatingColor}
        items={items}
        spacing={spacing}
        subtitle={subtitle}
        title={title}
        type={type}
        onSearchSuccess={onSearchSuccess}
      >
        {!disableCta && !isBrowsePage ? (
          <SectionSpacer
            edge={BOTTOM_EDGE}
            spacing={isValue(spacing) ? spacing / goldenRatio : undefined}
          >
            <SectionButtonCta component={BrowseLink} onClick={onItemClick}>
              Browse by any job
            </SectionButtonCta>
          </SectionSpacer>
        ) : null}
      </ContentSection>
    );
  },
);

JobTitlesSection.propTypes = {
  className: PropTypes.string,
  disableCta: PropTypes.bool,
  isAlternatingColor: PropTypes.bool,
  onItemClick: PropTypes.func,
  onSearchSuccess: PropTypes.func,
  spacing: PropTypes.number,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
};

JobTitlesSection.defaultProps = {
  title: `$(verbWords.capitalized) <em>your</em> $(resumeWords.singular) <b>professionally</b> with $(brand.title)`,
  subtitle: `Use our recruiter-approved suggestions for free`,
};

export default React.memo(
  withTrackingContext(JobTitlesSection, JOB_TITLES_SECTION_ID, { contextPropKey: 'type' }),
);
